import Cal, { getCalApi } from '@calcom/embed-react'
import { useEffect, useRef } from 'react'
import { useTheme } from '@/components/theme-provider'
import type { CalcomEmbedTarget } from '@/lib/audition/calcom-link'

// One namespace so the `on`/`off` below address this embed and not a
// second cal.com widget somewhere else on the site.
const NAMESPACE = 'audition'
const BOOKED = 'bookingSuccessfulV2'

/**
 * The cal.com booking page inline (LG-1033), pointed at this applicant by
 * `audition.bookingEmbed`. `onBooked` is a nudge only: the stage moves
 * when cal.com's webhook lands, and the caller polls for that rather than
 * trusting the iframe's word.
 */
export function BookingEmbed({
  target,
  onBooked,
}: {
  target: CalcomEmbedTarget
  onBooked: () => void
}) {
  const { resolvedTheme } = useTheme()
  const onBookedRef = useRef(onBooked)
  onBookedRef.current = onBooked
  const embedJsUrl = target.calOrigin
    ? `${target.calOrigin}/embed/embed.js`
    : undefined

  useEffect(() => {
    let cancelled = false
    let api: Awaited<ReturnType<typeof getCalApi>> | undefined
    const callback = () => onBookedRef.current()
    getCalApi({ namespace: NAMESPACE, embedJsUrl })
      .then((cal) => {
        if (cancelled) return
        api = cal
        cal('on', { action: BOOKED, callback })
      })
      // A blocked or failed embed script rejects here. `void` alone would
      // leave that as an unhandled rejection, and there is nothing to
      // recover: the listener is a nudge, the stage still moves on cal.com's
      // webhook, and the caller's own polling is what the applicant sees.
      // Warn so a broken script is visible, and let the `<Cal>` below report
      // the load failure it owns.
      .catch((error: unknown) => {
        console.warn('[audition] cal.com embed API unavailable:', error)
      })
    return () => {
      cancelled = true
      api?.('off', { action: BOOKED, callback })
    }
  }, [embedJsUrl])

  return (
    <Cal
      namespace={NAMESPACE}
      calLink={target.calLink}
      calOrigin={target.calOrigin ?? undefined}
      embedJsUrl={embedJsUrl}
      config={{
        ...target.prefill,
        layout: 'month_view',
        theme: resolvedTheme === 'dark' ? 'dark' : 'light',
      }}
      style={{ width: '100%', height: '100%', overflow: 'scroll' }}
    />
  )
}
